import React, { useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import { GeckoComponentDataFrame, GeckoComponentRect } from 'types'

import { useCdfDataFetcher } from './useCdfDataFetcher'
import { gcrToBinSelection } from './useCdfDataGenerator'

/**
 * A hook that calls the fetcher returned by useGetCdf2dDataFetcher() using SWR
 * to get a 2D-array of the data for a given CDF where the index of the outer
 * array is a y-index, and the index of the inner array is an x-index.
 *
 * TODO: optimize this function (or make obsolete with 1d array handling)
 *  -- This function Out-of-memory errors when called many times on RAUT assets
 *
 * @param cdf             The component data frame to pull data from
 * @param cropSelection An optional x/y area to select data from.
 * @returns               An array-of-arrays-of-numbers as described above
 */
export function useCdfRawValues2DArray(
  cdf: GeckoComponentDataFrame | undefined,
  cropSelection?: GeckoComponentRect
): {
  array: ReadonlyArray<ReadonlyArray<number>> | undefined
  isLoading: boolean
} {
  const cdfData2dFetcherBase = useGetCdf2dDataFetcher()
  const cdfData2dFetcherNoParams = React.useCallback(
    async () => ({
      array: cdf ? await cdfData2dFetcherBase(cdf, cropSelection) : undefined,
      id: cdf?.id,
      generation: cdf?.generation,
      cropSelection: cropSelection,
    }),
    [cdf, cdfData2dFetcherBase, cropSelection]
  )

  const { data, isLoading } = useSWRImmutable(
    // This '2d' is needed so SWR doesn't conflate the 1d and 2d raw-value fetch keys
    [cdf?.id, cdf?.generation, cropSelection, '2d'],
    cdfData2dFetcherNoParams,
    // There is no cheap way to compare CDF data to know if it has changed,
    // and SWR will attempt to use stableHash... which involves iterating over
    // all elements in the returned array. Very slow. For more information,
    // see: https://github.com/vercel/swr/issues/1719
    //
    // Instead, check if the cdf-id or generation has changed.
    {
      compare: (a, b) =>
        (a?.id ?? '') + (a?.generation?.toString() ?? '') ===
          (b?.id ?? '') + (b?.generation?.toString() ?? '') &&
        GeckoComponentRect.Equals(a?.cropSelection, b?.cropSelection),
    }
  )

  return useMemo(
    () => ({
      array: data?.array,
      isLoading,
    }),
    [data, isLoading]
  )
}

/**
 * A hook that returns a callback function that take the result of useCdfRawValues()
 * and inflate it into a 2D array, consisting of an array of arrays where the
 * index of the outer array is a y-index, and the index of the inner array is an
 * x-index.
 *
 * @param allowCache    If false, the cache will be ignored and the data will only be fetched from rolodex-proper.
 *                      This will also make it so that the returned function will not re-initialize on cache change, which
 *                      is needed for when we're fetching data that will be used to generate something for the Rolodex-Cache,
 *                      as if we don't ignore the cache in memos we'll end up in an infinite loop.
 *
 * TODO: optimize this function (or make obsolete with 1d array handling)
 *  -- This function Out-of-memory errors when called many times on RAUT assets
 *
 * @returns     A function with two parameters, `cdf: GeckoComponentDataFrame` cropSelection: GeckoComponentRect`,
 *              that returns the data for the given `cdf` with optional `cropSelection` filter applied as a 2d-array
 *              (as a `Promise<ReadonlyArray<ReadonlyArray<number>>>`)
 *              or `undefined` if it couldn't be fetched
 */
export function useGetCdf2dDataFetcher(): (
  cdf: GeckoComponentDataFrame,
  cropSelection?: GeckoComponentRect
) => Promise<ReadonlyArray<ReadonlyArray<number>> | undefined> {
  const cdfDataFetcher = useCdfDataFetcher()
  /**
   * A function that fetches the data as a 2d-array for a given CDF
   *
   * @param cdf       The CDF to fetch data for
   * @param cropSelection An optional x/y area to select data from.
   * @returns         The data for the CDF, or undefined if it couldn't be fetched
   */
  return React.useCallback(
    async (
      cdf: GeckoComponentDataFrame,
      cropSelection?: GeckoComponentRect
    ) => {
      const buffer = await cdfDataFetcher(cdf, cropSelection)
      const binSelection = gcrToBinSelection(cropSelection, cdf)
      if (!buffer) {
        return undefined
      }

      const xLen = binSelection
        ? binSelection.x_end - binSelection.x_start
        : cdf.data.binning.x_bin_count
      const yLen = binSelection
        ? binSelection.y_end - binSelection.y_start
        : cdf.data.binning.y_bin_count

      const resultArr = new Array<number[]>(yLen)
      for (let i = 0; i < yLen; i += 1) {
        resultArr[i] = new Array<number>(xLen).fill(Number.NaN)
      }
      for (let index = 0; index < buffer.values.length; index += 1) {
        const value = buffer.values.at(index) ?? Number.NaN
        const yIdx = Math.floor(index / xLen)
        const xIdx = index % xLen
        resultArr[yIdx][xIdx] = value
      }
      return resultArr
    },
    [cdfDataFetcher]
  )
}
